import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useTheme } from "../context/ThemeContext";
import { Lock, KeyRound, ShieldCheck, ArrowRight, Loader2, Trash2, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import LocationPicker from "../components/LocationPicker";
import TotpEnrolSection from "../components/TotpEnrolSection";
import OpeningBalanceCard from "../components/mshop/OpeningBalanceCard";

// Accounts that follow the "TOTP-optional + Settings-driven enrolment" flow.
// Mirrors the backend's TOTP_OPTIONAL_COMPANIES set.
const TOTP_OPTIONAL_COMPANIES = new Set([
    "المنصوري للذهب",
    "الحميري للذهب",
    "الراقية للذهب",
    "الراقيه للذهب",
]);

export default function Settings() {
    const { company, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const { t, lang, toggle: toggleLang } = useLang();
    const { theme, setTheme } = useTheme();

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");


    // Location setup state (only shown for legacy accounts without a location)
    const [locCountry, setLocCountry] = useState("");
    const [locCountryCode, setLocCountryCode] = useState("");
    const [locCity, setLocCity] = useState("");
    const [locError, setLocError] = useState("");
    const [locLoading, setLocLoading] = useState(false);

    // Deletion code state
    const [codeCurrent, setCodeCurrent] = useState("");
    const [newCode, setNewCode] = useState("");
    const [confirmCode, setConfirmCode] = useState("");
    const [codeLoading, setCodeLoading] = useState(false);
    const [codeError, setCodeError] = useState("");

    // Delete account state
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    // Ledger mode (one-time pick)
    const [modeChoice, setModeChoice] = useState("");
    const [modeError, setModeError] = useState("");
    const [modeLoading, setModeLoading] = useState(false);

    // Account type (one-time pick) — manager / individual
    const [acctChoice, setAcctChoice] = useState("");
    const [acctError, setAcctError] = useState("");
    const [acctLoading, setAcctLoading] = useState(false);

    // Manager company-access form
    const [empLimit, setEmpLimit] = useState("");
    const [accessLoading, setAccessLoading] = useState(false);
    const [accessError, setAccessError] = useState("");

    const submitAccountType = async (e) => {
        e.preventDefault();
        setAcctError("");
        if (acctChoice !== "manager" && acctChoice !== "individual") {
            setAcctError(t.accountTypeRequired);
            return;
        }
        setAcctLoading(true);
        try {
            await api.post("/auth/set-account-type", { account_type: acctChoice });
            await refresh();
            toast.success(t.ledgerModeSaved);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setAcctError(msg);
            toast.error(msg);
        } finally {
            setAcctLoading(false);
        }
    };

    const submitCompanyAccess = async (e) => {
        e.preventDefault();
        setAccessError("");
        const n = parseInt(empLimit, 10);
        if (!Number.isFinite(n) || n < 1) {
            setAccessError(t.accountTypeRequired);
            return;
        }
        setAccessLoading(true);
        try {
            await api.post("/auth/set-company-access", {
                employee_limit: n,
            });
            await refresh();
            setEmpLimit("");
            toast.success(t.managerCompanyAccessSaved);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setAccessError(msg);
            toast.error(msg);
        } finally {
            setAccessLoading(false);
        }
    };

    const submitLedgerMode = async (e) => {
        e.preventDefault();
        setModeError("");
        if (modeChoice !== "full" && modeChoice !== "basic") {
            setModeError(t.ledgerModeRequired);
            return;
        }
        setModeLoading(true);
        try {
            await api.post("/auth/set-ledger-mode", { mode: modeChoice });
            await refresh();
            toast.success(t.ledgerModeSaved);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setModeError(msg);
            toast.error(msg);
        } finally {
            setModeLoading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");

        if (!current || !next || !confirm) {
            setError(t.allFieldsRequired);
            return;
        }
        if (next.length < 4) {
            setError(t.newPasswordTooShort);
            return;
        }
        if (next !== confirm) {
            setError(t.passwordsDontMatch);
            return;
        }
        if (next === current) {
            setError(t.newPasswordSameAsCurrent);
            return;
        }

        setLoading(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success(t.passwordChangedToast);
            await logout();
            navigate("/login", { replace: true });
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message || t.changePasswordFail;
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const submitLocation = async (e) => {
        e.preventDefault();
        setLocError("");
        if (!locCountry || !locCity) {
            setLocError(`${t.countryLabel} / ${t.cityLabel}`);
            return;
        }
        setLocLoading(true);
        try {
            await api.post("/auth/set-location", { country: locCountry, country_code: locCountryCode, city: locCity });
            await refresh();
            toast.success(t.locationSavedToast);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setLocError(msg);
            toast.error(msg);
        } finally {
            setLocLoading(false);
        }
    };

    const submitDeletionCode = async (e) => {
        e.preventDefault();
        setCodeError("");
        if (!codeCurrent || !newCode || !confirmCode) {
            setCodeError(t.allFieldsRequired);
            return;
        }
        if (newCode.length < 4) {
            setCodeError(t.deletionCodeShort);
            return;
        }
        if (newCode !== confirmCode) {
            setCodeError(t.codesDontMatch);
            return;
        }
        setCodeLoading(true);
        try {
            await api.post("/auth/set-deletion-code", {
                current_password: codeCurrent,
                new_code: newCode,
            });
            toast.success(t.deletionCodeUpdated);
            setCodeCurrent("");
            setNewCode("");
            setConfirmCode("");
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message || t.deletionCodeUpdateFail;
            setCodeError(msg);
            toast.error(msg);
        } finally {
            setCodeLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteError("");
        if (!deletePassword) {
            setDeleteError(t.passwordRequired);
            return;
        }
        setDeleting(true);
        try {
            await api.post("/auth/delete-account", { password: deletePassword });
            toast.success(t.accountDeletedToast);
            // cookie is cleared server-side; sync client state + navigate
            await logout();
            navigate("/login", { replace: true });
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message || t.accountDeleteFail;
            setDeleteError(msg);
            toast.error(msg);
            setDeleting(false);
        }
    };

    return (
        <AppShell>
            <div className="mb-4 sm:mb-6">
                <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900" data-testid="back-from-settings">
                    <ArrowRight className="h-4 w-4" />
                    {t.backToLedger}
                </Link>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.settingsKicker}</p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{t.settingsTitle}</h1>
                <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                    {t.settingsSubtitle} <span className="font-semibold text-neutral-700">{company?.company_name}</span>.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_2fr]">
                {/* Left column: info card */}
                <aside className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <h3 className="mt-3 font-heading text-base font-bold">{t.accountSecurity}</h3>
                    <p className="mt-1 text-sm text-neutral-500">{t.accountSecurityHint}</p>
                    <ul className="mt-4 space-y-2 text-xs text-neutral-500">
                        <li className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {t.secTip1}
                        </li>
                        <li className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {t.secTip2}
                        </li>
                        <li className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {t.secTip3}
                        </li>
                    </ul>
                </aside>

                {/* Right column: form */}
                <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6">
                    <h2 className="font-heading text-lg font-bold">{t.changePassword}</h2>
                    <p className="mt-1 text-sm text-neutral-500">{t.changePasswordHint}</p>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">{t.currentPassword}</Label>
                            <div className="relative">
                                <Lock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                                <Input
                                    type="password"
                                    value={current}
                                    onChange={(e) => setCurrent(e.target.value)}
                                    className="h-11 pe-10 ps-3"
                                    autoComplete="current-password"
                                    required
                                    data-testid="current-password-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">{t.newPassword}</Label>
                            <div className="relative">
                                <KeyRound className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                                <Input
                                    type="password"
                                    value={next}
                                    onChange={(e) => setNext(e.target.value)}
                                    className="h-11 pe-10 ps-3"
                                    autoComplete="new-password"
                                    required
                                    data-testid="new-password-input"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">{t.confirmNewPassword}</Label>
                            <Input
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="h-11 ps-3"
                                autoComplete="new-password"
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>

                        {error ? (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="settings-error">
                                {error}
                            </div>
                        ) : null}

                        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setCurrent("");
                                    setNext("");
                                    setConfirm("");
                                    setError("");
                                }}
                                data-testid="settings-clear-btn"
                            >
                                {t.clearBtn}
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="bg-neutral-900 text-white hover:bg-neutral-800"
                                data-testid="change-password-submit-btn"
                            >
                                {loading ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                                {t.saveNewPassword}
                            </Button>
                        </div>
                    </form>
                </section>
            </div>

            {/* Preferences — language + theme (#2, #3) */}
            <section
                className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6"
                data-testid="preferences-section"
            >
                <h2 className="font-heading text-lg font-bold">{t.preferencesTitle}</h2>
                <p className="mt-1 text-sm text-neutral-500">{t.preferencesHint}</p>

                <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                    {/* Language toggle */}
                    <div>
                        <Label className="mb-2 block text-sm font-medium">{t.languageLabel}</Label>
                        <div className="grid grid-cols-2 gap-2" role="group">
                            {[
                                { code: "ar", label: "العربية" },
                                { code: "en", label: "English" },
                            ].map((opt) => (
                                <button
                                    key={opt.code}
                                    type="button"
                                    onClick={() => lang !== opt.code && toggleLang()}
                                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                        lang === opt.code
                                            ? "border-amber-600 bg-amber-50 text-amber-800 ring-1 ring-amber-300"
                                            : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                                    }`}
                                    data-testid={`pref-lang-${opt.code}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Theme toggle */}
                    <div>
                        <Label className="mb-2 block text-sm font-medium">{t.themeLabel}</Label>
                        <div className="grid grid-cols-2 gap-2" role="group">
                            {[
                                { code: "light", label: t.themeLight },
                                { code: "dark", label: t.themeDark },
                            ].map((opt) => (
                                <button
                                    key={opt.code}
                                    type="button"
                                    onClick={() => setTheme(opt.code)}
                                    className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                        theme === opt.code
                                            ? "border-amber-600 bg-amber-50 text-amber-800 ring-1 ring-amber-300"
                                            : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                                    }`}
                                    data-testid={`pref-theme-${opt.code}`}
                                >
                                    {opt.code === "dark" ? "🌙" : "☀️"}
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>


            {/* TOTP enrolment — only for whitelisted accounts that haven't enrolled yet (#95) */}
            {TOTP_OPTIONAL_COMPANIES.has(company?.company_name) && !company?.totp_enabled ? (
                <TotpEnrolSection />
            ) : null}

            {/* Location — hidden for employees (#84) */}
            {company?.account_type !== "employee" ? (
            <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6" data-testid="location-section">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        <MapPin className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-lg font-bold">{t.locationLabel}</h2>
                        {company?.country && company?.city ? (
                            <div className="mt-3" data-testid="location-locked">
                                <LocationPicker
                                    country={company.country}
                                    countryCode={company.country_code}
                                    city={company.city}
                                    disabled
                                    testIdPrefix="settings-loc-locked"
                                />
                            </div>
                        ) : (
                            <>
                                <p className="mt-1 text-sm text-neutral-500">{t.locationSetHint}</p>
                                <form onSubmit={submitLocation} className="mt-4 space-y-3" data-testid="location-set-form">
                                    <LocationPicker
                                        country={locCountry}
                                        countryCode={locCountryCode}
                                        city={locCity}
                                        onChange={({ country: c, countryCode: cc, city: ct }) => {
                                            setLocCountry(c);
                                            setLocCountryCode(cc);
                                            setLocCity(ct);
                                        }}
                                        testIdPrefix="settings-loc"
                                    />

                                    {locError ? (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="location-set-error">
                                            {locError}
                                        </div>
                                    ) : null}

                                    <div className="flex justify-end">
                                        <Button
                                            type="submit"
                                            disabled={locLoading}
                                            className="bg-neutral-900 text-white hover:bg-neutral-800"
                                            data-testid="location-save-btn"
                                        >
                                            {locLoading ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <MapPin className="ms-1 h-4 w-4" />}
                                            {t.saveLocation}
                                        </Button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </section>
            ) : null}

            {!company?.mansori_shop && company?.account_type !== "employee" && (company?.enabled_ledgers || []).includes("daily") ? (<>
            {/* Ledger Mode (one-time pick) */}
            <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6" data-testid="ledger-mode-section">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        <Lock className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-lg font-bold">{t.ledgerModeLabel}</h2>
                        {company?.ledger_mode ? (
                            <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800" data-testid="ledger-mode-locked">
                                <Lock className="h-4 w-4 shrink-0" />
                                <span className="font-semibold">
                                    {company.enabled_ledgers?.length
                                        ? company.enabled_ledgers.map((k) => ({ factory: "دفتر المصانع", client: "دفتر العملاء", daily: "دفتر الحركة", vouchers: "سندات الصرف" }[k])).filter(Boolean).join(" + ")
                                        : company.ledger_mode === "full" ? t.ledgerModeFull : t.ledgerModeBasic}
                                </span>
                                <span className="ms-auto text-[11px] text-emerald-600/70">{t.ledgerModeLocked}</span>
                            </div>
                        ) : (
                            <>
                                <p className="mt-1 text-sm text-neutral-500">{t.ledgerModeSettingsHint}</p>
                                <form onSubmit={submitLedgerMode} className="mt-4 space-y-3" data-testid="ledger-mode-set-form">
                                    <button
                                        type="button"
                                        onClick={() => setModeChoice("full")}
                                        data-testid="settings-ledger-mode-full"
                                        aria-pressed={modeChoice === "full"}
                                        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-start text-sm transition ${
                                            modeChoice === "full"
                                                ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                                                : "border-neutral-200 bg-white hover:border-amber-300"
                                        }`}
                                    >
                                        <span className="font-medium text-neutral-800">{t.ledgerModeFull}</span>
                                        <span
                                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                                modeChoice === "full" ? "border-amber-600 bg-amber-600" : "border-neutral-300"
                                            }`}
                                            aria-hidden
                                        >
                                            {modeChoice === "full" ? <span className="block h-2 w-2 rounded-full bg-white" /> : null}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setModeChoice("basic")}
                                        data-testid="settings-ledger-mode-basic"
                                        aria-pressed={modeChoice === "basic"}
                                        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-start text-sm transition ${
                                            modeChoice === "basic"
                                                ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                                                : "border-neutral-200 bg-white hover:border-amber-300"
                                        }`}
                                    >
                                        <span className="font-medium text-neutral-800">{t.ledgerModeBasic}</span>
                                        <span
                                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                                modeChoice === "basic" ? "border-amber-600 bg-amber-600" : "border-neutral-300"
                                            }`}
                                            aria-hidden
                                        >
                                            {modeChoice === "basic" ? <span className="block h-2 w-2 rounded-full bg-white" /> : null}
                                        </span>
                                    </button>
                                    {modeError ? (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="ledger-mode-error">
                                            {modeError}
                                        </div>
                                    ) : null}
                                    <Button
                                        type="submit"
                                        disabled={modeLoading}
                                        className="h-11 bg-neutral-900 text-white hover:bg-neutral-800"
                                        data-testid="ledger-mode-save-btn"
                                    >
                                        {modeLoading ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                                        {t.saveLedgerMode}
                                    </Button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </section>
            </>) : null}

            {/* Daily Ledger Variant (one-time pick — only when daily is enabled) */}
            {company?.ledger_mode === "full" && company?.account_type !== "manager" && company?.account_type !== "employee" && !company?.mansori_shop && (company?.enabled_ledgers || []).includes("daily") ? (
                <section
                    className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6"
                    data-testid="daily-variant-section"
                >
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                            <Lock className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <h2 className="font-heading text-lg font-bold">{t.dvTitle}</h2>
                            {company?.daily_variant ? (
                                <div
                                    className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800"
                                    data-testid="daily-variant-locked"
                                >
                                    <Lock className="h-4 w-4 shrink-0" />
                                    <span className="font-semibold">
                                        {company.daily_variant === "advanced"
                                            ? t.dvAdvancedTitle
                                            : t.dvStandardTitle}
                                    </span>
                                    <span className="ms-auto text-[11px] text-emerald-600/70">
                                        {t.dvLockedHint}
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <p className="mt-1 text-sm text-neutral-500">{t.dvSubtitle}</p>
                                    <Button
                                        type="button"
                                        onClick={() => navigate("/daily/choose")}
                                        className="mt-4 h-11 gap-2 bg-indigo-700 hover:bg-indigo-800"
                                        data-testid="daily-variant-open-btn"
                                    >
                                        {t.dvSelect}
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* Shop opening balances — visible for gold accounts (non-car-learn, non-manager, non-employee).
                Managers still have opening balances but they don't have the personal daily flow. */}
            {!company?.car_learn && company?.role !== "admin" && company?.account_type !== "employee" && (company?.account_type === "manager" || (company?.enabled_ledgers || []).includes("daily")) ? (
                <OpeningBalancesSection company={company} refresh={refresh} lang={lang} />
            ) : null}
            {company?.mansori_shop ? <OpeningBalanceCard /> : null}

            {!company?.mansori_shop && company?.account_type !== "employee" ? (<>
            {/* Deletion Code */}
            <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6" data-testid="account-type-section">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        <Lock className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-lg font-bold">{t.accountTypeTitle}</h2>
                        {company?.account_type && company.account_type !== "employee" ? (
                            <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800" data-testid="account-type-locked">
                                <Lock className="h-4 w-4 shrink-0" />
                                <span className="font-semibold">
                                    {company.account_type === "manager" ? t.accountTypeManager : t.accountTypeIndividual}
                                </span>
                                <span className="ms-auto text-[11px] text-emerald-600/70">{t.accountTypeLocked}</span>
                            </div>
                        ) : company?.account_type === "employee" ? null : (
                            <>
                                <p className="mt-1 text-sm text-neutral-500">{t.accountTypeSettingsHint}</p>
                                <form onSubmit={submitAccountType} className="mt-4 space-y-3" data-testid="account-type-form">
                                    <button
                                        type="button"
                                        onClick={() => setAcctChoice("manager")}
                                        data-testid="settings-acct-manager"
                                        aria-pressed={acctChoice === "manager"}
                                        className={`flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-3 text-start text-sm transition ${
                                            acctChoice === "manager" ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" : "border-neutral-200 bg-white hover:border-amber-300"
                                        }`}
                                    >
                                        <div>
                                            <div className="font-semibold text-neutral-800">{t.accountTypeManager}</div>
                                            <div className="mt-0.5 text-xs text-neutral-500">{t.accountTypeManagerDesc}</div>
                                        </div>
                                        <span className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${acctChoice === "manager" ? "border-amber-600 bg-amber-600" : "border-neutral-300"}`} aria-hidden>
                                            {acctChoice === "manager" ? <span className="block h-2 w-2 rounded-full bg-white" /> : null}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAcctChoice("individual")}
                                        data-testid="settings-acct-individual"
                                        aria-pressed={acctChoice === "individual"}
                                        className={`flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-3 text-start text-sm transition ${
                                            acctChoice === "individual" ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" : "border-neutral-200 bg-white hover:border-amber-300"
                                        }`}
                                    >
                                        <div>
                                            <div className="font-semibold text-neutral-800">{t.accountTypeIndividual}</div>
                                            <div className="mt-0.5 text-xs text-neutral-500">{t.accountTypeIndividualDesc}</div>
                                        </div>
                                        <span className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${acctChoice === "individual" ? "border-amber-600 bg-amber-600" : "border-neutral-300"}`} aria-hidden>
                                            {acctChoice === "individual" ? <span className="block h-2 w-2 rounded-full bg-white" /> : null}
                                        </span>
                                    </button>
                                    {acctError ? (
                                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="account-type-error">{acctError}</div>
                                    ) : null}
                                    <Button type="submit" disabled={acctLoading} className="h-11 bg-neutral-900 text-white hover:bg-neutral-800" data-testid="account-type-save">
                                        {acctLoading ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                                        {t.saveAccountType}
                                    </Button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </section>
            </>) : null}

            {/* Manager: Company access settings */}
            {company?.account_type === "manager" ? (
                <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/40 p-5 shadow-sm sm:p-6" data-testid="manager-access-section">
                    <h2 className="font-heading text-lg font-bold">{t.managerCompanyAccessTitle}</h2>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{t.managerCompanyCodeLabel}</div>
                            <div className="mt-0.5 font-numbers text-2xl font-bold tracking-widest text-neutral-900" data-testid="manager-code-chip">{company.company_code || "—"}</div>
                            <p className="mt-0.5 text-xs text-neutral-500">{t.managerCompanyCodeHint}</p>
                        </div>
                        <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">{t.managerEmployeeLimitLabel}</div>
                            <div className="mt-0.5 font-numbers text-2xl font-bold text-neutral-900">{company.employee_limit || 0}</div>
                        </div>
                    </div>
                    <form onSubmit={submitCompanyAccess} className="mt-4 space-y-3" data-testid="manager-access-form">
                        <div>
                            <Label className="mb-1.5 block text-sm font-medium">{t.managerEmployeeLimitLabel}</Label>
                            <Input
                                type="number"
                                min={1}
                                max={999}
                                value={empLimit}
                                onChange={(e) => setEmpLimit(e.target.value)}
                                className="h-11 font-numbers"
                                placeholder={String(company.employee_limit || 5)}
                                required
                                data-testid="manager-limit-input"
                            />
                        </div>
                        {accessError ? (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="manager-access-error">{accessError}</div>
                        ) : null}
                        <Button type="submit" disabled={accessLoading} className="h-11 bg-neutral-900 text-white hover:bg-neutral-800" data-testid="manager-access-save">
                            {accessLoading ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}
                            {t.managerSaveAccess}
                        </Button>
                    </form>
                </section>
            ) : null}

            {/* Deletion Code (original) */}
            <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6" data-testid="deletion-code-section">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        <KeyRound className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-heading text-lg font-bold">{t.deletionCodeTitle}</h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            {t.deletionCodeHint}
                        </p>

                        <form onSubmit={submitDeletionCode} className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="deletion-code-form">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.currentPassword}</Label>
                                <Input
                                    type="password"
                                    value={codeCurrent}
                                    onChange={(e) => setCodeCurrent(e.target.value)}
                                    autoComplete="current-password"
                                    className="h-11 ps-3"
                                    data-testid="code-current-password-input"
                                />
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.newDeletionCode}</Label>
                                <Input
                                    type="password"
                                    value={newCode}
                                    onChange={(e) => setNewCode(e.target.value)}
                                    autoComplete="new-password"
                                    className="h-11 ps-3"
                                    data-testid="new-deletion-code-input"
                                />
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.confirmDeletionCode}</Label>
                                <Input
                                    type="password"
                                    value={confirmCode}
                                    onChange={(e) => setConfirmCode(e.target.value)}
                                    autoComplete="new-password"
                                    className="h-11 ps-3"
                                    data-testid="confirm-deletion-code-input"
                                />
                            </div>

                            {codeError ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-3" data-testid="deletion-code-error">
                                    {codeError}
                                </div>
                            ) : null}

                            <div className="flex justify-end sm:col-span-3">
                                <Button
                                    type="submit"
                                    disabled={codeLoading}
                                    className="bg-neutral-900 text-white hover:bg-neutral-800"
                                    data-testid="save-deletion-code-btn"
                                >
                                    {codeLoading ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <KeyRound className="ms-1 h-4 w-4" />}
                                    {t.saveDeletionCode}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            {!company?.mansori_shop ? (<>
            {/* Danger zone — Delete Account */}
            <section className="mt-6 rounded-2xl border border-red-200 bg-red-50/40 p-5 shadow-sm sm:p-6" data-testid="delete-account-section">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-700 ring-1 ring-red-200">
                            <AlertTriangle className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="font-heading text-base font-bold text-red-900">{t.dangerZoneTitle}</h3>
                            <p className="mt-1 text-sm text-red-800/80">
                                {t.dangerZoneHint1}
                                <br />
                                {t.dangerZoneHint2} <span className="font-semibold">&ldquo;{company?.company_name}&rdquo;</span> {t.dangerZoneHint3}
                            </p>
                        </div>
                    </div>

                    <AlertDialog
                        open={deleteOpen}
                        onOpenChange={(v) => {
                            setDeleteOpen(v);
                            if (!v) {
                                setDeletePassword("");
                                setDeleteError("");
                            }
                        }}
                    >
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="outline"
                                className="shrink-0 border-red-300 bg-white text-red-700 hover:bg-red-50 hover:text-red-800"
                                data-testid="open-delete-account-btn"
                            >
                                <Trash2 className="ms-1 h-4 w-4" />
                                {t.deleteAccount}
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent dir={t.dir} data-testid="delete-account-dialog">
                            <AlertDialogHeader className="text-start">
                                <AlertDialogTitle className="flex items-center gap-2 text-red-800">
                                    <AlertTriangle className="h-5 w-5" />
                                    {t.deleteAccountConfirmTitle}
                                </AlertDialogTitle>
                                <AlertDialogDescription className="space-y-2">
                                    <span className="block">
                                        {t.actionWillDelete} <span className="font-bold text-red-700">{t.irreversible}</span>. {t.sectorsDeleted}
                                    </span>
                                    <ul className="list-disc space-y-0.5 pe-5 text-sm">
                                        <li>{t.bullet1}</li>
                                        <li>{t.bullet2}</li>
                                    </ul>
                                    <span className="block">{t.enterPasswordToConfirm}</span>
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <div className="mt-2">
                                <Label className="mb-1.5 block text-sm font-medium">{t.passwordLabel}</Label>
                                <Input
                                    type="password"
                                    value={deletePassword}
                                    onChange={(e) => setDeletePassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleDeleteAccount();
                                        }
                                    }}
                                    autoComplete="current-password"
                                    className="h-11"
                                    data-testid="delete-account-password-input"
                                    placeholder="••••••••"
                                    autoFocus
                                />
                                {deleteError ? (
                                    <div className="mt-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-red-700" data-testid="delete-account-error">
                                        {deleteError}
                                    </div>
                                ) : null}
                            </div>
                            <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                                <AlertDialogCancel disabled={deleting} data-testid="delete-account-cancel-btn">
                                    {t.cancel}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={(e) => {
                                        e.preventDefault(); // keep dialog open on validation errors
                                        handleDeleteAccount();
                                    }}
                                    disabled={deleting}
                                    className="bg-red-600 text-white hover:bg-red-700"
                                    data-testid="delete-account-confirm-btn"
                                >
                                    {deleting ? (
                                        <Loader2 className="ms-1 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="ms-1 h-4 w-4" />
                                    )}
                                    {t.deleteForever}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </section>
            </>) : null}
        </AppShell>
    );
}


/** Editable opening balances for the shop (gold weight + cash). Changing
 *  these values re-bases every calculation in سجل المحل, so the save button
 *  is guarded by an explicit confirm dialog with a strong warning. */
function OpeningBalancesSection({ company, refresh, lang }) {
    const ar = lang === "ar";
    const [loaded, setLoaded] = useState(false);
    const [saved, setSaved] = useState(null); // {gold, cash} when already saved, else null
    const [editing, setEditing] = useState(false);
    const [gold, setGold] = useState("");
    const [cash, setCash] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/shop/opening-balances");
            setSaved(r.data.ever_set ? { gold: r.data.opening_gold_grams, cash: r.data.opening_cash } : null);
        } catch { /* silent */ }
        finally { setLoaded(true); }
    };
    useEffect(() => { load(); }, []);

    const doSave = async () => {
        const g = parseFloat(gold), c = parseFloat(cash);
        if (!Number.isFinite(g) || g < 0) { toast.error(ar ? "أدخل وزن الذهب" : "Enter gold weight"); return; }
        if (!Number.isFinite(c) || c < 0) { toast.error(ar ? "أدخل الكاش" : "Enter cash"); return; }
        setBusy(true);
        try {
            await api.post("/shop/opening-balances", { opening_gold_grams: g, opening_cash: c, confirm_edit: !!saved });
            toast.success(ar ? "تم حفظ الرصيد الابتدائي" : "Opening balances saved");
            setConfirmOpen(false);
            setEditing(false);
            await load();
            if (refresh) await refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail || e)); }
        finally { setBusy(false); }
    };

    const startEdit = () => {
        setGold(saved ? String(saved.gold) : "");
        setCash(saved ? String(saved.cash) : "");
        setEditing(true);
    };
    const fmt = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 3 });

    return (
        <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6" data-testid="opening-balances-section">
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                    <KeyRound className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h2 className="font-heading text-lg font-bold">{ar ? "الرصيد الابتدائي للمحل" : "Shop opening balances"}</h2>
                    <p className="mt-1 text-sm text-neutral-500">
                        {ar ? "يُحفظ مرة واحدة ويُستخدم كنقطة انطلاق لحسابات سجل المحل." : "Saved once and used as the base for Shop Log calculations."}
                    </p>
                    {!loaded ? (
                        <div className="mt-4 text-xs text-neutral-400"><Loader2 className="inline h-3.5 w-3.5 animate-spin" /></div>
                    ) : (
                        <>
                            {saved && !editing ? (
                                <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="opening-saved-values">
                                    <div className="rounded-xl bg-neutral-50 px-4 py-3 ring-1 ring-neutral-200">
                                        <div className="text-xs font-bold text-neutral-500">{ar ? "وزن الذهب الابتدائي (جرام)" : "Opening gold (g)"}</div>
                                        <div className="mt-1 font-numbers text-xl font-bold" data-testid="opening-saved-gold">{fmt(saved.gold)}</div>
                                    </div>
                                    <div className="rounded-xl bg-neutral-50 px-4 py-3 ring-1 ring-neutral-200">
                                        <div className="text-xs font-bold text-neutral-500">{ar ? "الكاش الابتدائي" : "Opening cash"}</div>
                                        <div className="mt-1 font-numbers text-xl font-bold" data-testid="opening-saved-cash">{fmt(saved.cash)}</div>
                                    </div>
                                </div>
                            ) : null}
                            {!saved && !editing ? (
                                <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="opening-not-set">
                                    {ar ? "لم يتم إدخال الرصيد الابتدائي بعد." : "Opening balances not entered yet."}
                                </div>
                            ) : null}
                            {editing ? (
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <div>
                                        <Label className="mb-1 block text-xs font-bold text-neutral-700">{ar ? "وزن الذهب الابتدائي (جرام)" : "Opening gold weight (g)"}</Label>
                                        <Input type="number" inputMode="decimal" step="0.001" value={gold} onChange={(e) => setGold(e.target.value)} className="font-numbers" data-testid="settings-opening-gold-input" />
                                    </div>
                                    <div>
                                        <Label className="mb-1 block text-xs font-bold text-neutral-700">{ar ? "الكاش الابتدائي" : "Opening cash"}</Label>
                                        <Input type="number" inputMode="decimal" step="0.001" value={cash} onChange={(e) => setCash(e.target.value)} className="font-numbers" data-testid="settings-opening-cash-input" />
                                    </div>
                                </div>
                            ) : null}
                            <div className="mt-4 flex flex-wrap gap-2">
                                {!editing ? (
                                    <Button onClick={startEdit} className="h-10 bg-emerald-700 hover:bg-emerald-800" data-testid={saved ? "settings-opening-edit-btn" : "settings-opening-enter-btn"}>
                                        {saved ? (ar ? "تعديل الرصيد الابتدائي" : "Edit opening balances") : (ar ? "إدخال الرصيد الابتدائي" : "Enter opening balances")}
                                    </Button>
                                ) : (
                                    <>
                                        <Button variant="outline" onClick={() => setEditing(false)} className="h-10" data-testid="settings-opening-cancel-edit">{ar ? "إلغاء" : "Cancel"}</Button>
                                        <Button onClick={() => (saved ? setConfirmOpen(true) : doSave())} disabled={busy || gold === "" || cash === ""} className="h-10 bg-emerald-700 hover:bg-emerald-800" data-testid="settings-opening-save-btn">
                                            {busy ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : null}{ar ? "حفظ" : "Save"}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                        <AlertDialogContent dir={ar ? "rtl" : "ltr"} data-testid="settings-opening-confirm">
                            <AlertDialogHeader className={ar ? "text-right sm:text-right" : ""}>
                                <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
                                    <AlertTriangle className="h-5 w-5" />
                                    {ar ? "تحذير" : "Warning"}
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-neutral-700">
                                    {ar ? "تعديل الرصيد الابتدائي سيؤثر على نتائج سجل المحل." : "Editing the opening balances will affect the Shop Log results."}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="settings-opening-cancel">{ar ? "إلغاء" : "Cancel"}</AlertDialogCancel>
                                <AlertDialogAction onClick={doSave} disabled={busy} className="bg-rose-700 hover:bg-rose-800" data-testid="settings-opening-confirm-btn">
                                    {ar ? "تأكيد التعديل" : "Confirm edit"}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </section>
    );
}
